import { escapeODataString, odataFilterParam } from '../lib/odataQuery';
import { normalizeMobileNumber } from '../config/keyRequest';

const OPENCARE_LOG_SELECT =
  'oc_opencarelogid,oc_id,oc_authorized,oc_phonenumber,oc_lockid,oc_jsonbody,oc_employee_name,' +
  'oc_eventtime,oc_logaction,oc_logsource,oc_sessionuserid,oc_sessionuseremail,oc_sessionusername,' +
  'oc_denialreason,oc_lockdisplayname,createdon,_oc_lock_value';

function toODataDate(iso) {
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }
  return parsed.toISOString().replace(/\.\d{3}Z$/, 'Z');
}

function eventTimestampFromRecord(record) {
  if (!record) {
    return null;
  }
  let body = record.oc_jsonbody;
  if (typeof body === 'string') {
    try {
      body = JSON.parse(body);
    } catch {
      body = null;
    }
  }
  const raw =
    record.oc_eventtime ||
    body?.timestamp ||
    body?.checkedAt ||
    body?.created ||
    body?.createdAt ||
    record.createdon ||
    null;
  if (!raw) {
    return null;
  }
  const ms = new Date(raw).getTime();
  return Number.isNaN(ms) ? null : ms;
}

export function createOpencareLogService(dataverseApiCall) {
  const listPage = async (path) => dataverseApiCall(path);

  return {
    /**
     * Lists oc_opencarelog rows. Server uses createdon >= from (no upper bound) so
     * backfilled Unloc events (createdon ≈ ingest time) still appear when viewing
     * older event periods. Callers should filter on event timestamp (jsonbody).
     */
    list: async ({ from, to, lockId, lockIds, phone, top = 500, filterByEventTime = true } = {}) => {
      const filters = ['statecode eq 0'];
      const fromOdata = from ? toODataDate(from) : null;
      const toOdata = to ? toODataDate(to) : null;

      // Prefer oc_eventtime (canonical). Fallback includes null eventtime + createdon for legacy rows.
      if (fromOdata) {
        filters.push(
          `(oc_eventtime ge ${fromOdata} or (oc_eventtime eq null and createdon ge ${fromOdata}))`
        );
      }
      if (toOdata) {
        filters.push(`(oc_eventtime le ${toOdata} or oc_eventtime eq null)`);
      }
      const lockIdList = [...new Set([...(lockIds || []), lockId].filter(Boolean))];
      if (lockIdList.length === 1) {
        const escaped = escapeODataString(lockIdList[0]);
        filters.push(`oc_lockid eq '${escaped}'`);
      } else if (lockIdList.length > 1) {
        const parts = lockIdList.map((id) => `oc_lockid eq '${escapeODataString(id)}'`);
        filters.push(`(${parts.join(' or ')})`);
      }
      if (phone) {
        const mobile = normalizeMobileNumber(phone) || phone;
        const escaped = escapeODataString(mobile);
        const withoutPlus = escapeODataString(String(mobile).replace(/^\+/, ''));
        filters.push(`(oc_phonenumber eq '${escaped}' or oc_phonenumber eq '${withoutPlus}')`);
      }

      const filter = odataFilterParam(filters.join(' and '));
      let path =
        `/oc_opencarelogs?$select=${OPENCARE_LOG_SELECT}` +
        `&$filter=${filter}&$orderby=createdon desc&$top=${top}`;

      const records = [];
      for (let page = 0; page < 40 && path; page += 1) {
        const result = await listPage(path);
        if (!result) {
          break;
        }
        records.push(...(result.value || []));
        path = result['@odata.nextLink'] || null;
      }

      if (!filterByEventTime) {
        return records;
      }

      const fromMs = from ? new Date(from).getTime() : null;
      const toMs = to ? new Date(to).getTime() : null;
      return records.filter((record) => {
        const at = eventTimestampFromRecord(record);
        if (at == null) {
          return true;
        }
        if (fromMs != null && !Number.isNaN(fromMs) && at < fromMs) {
          return false;
        }
        if (toMs != null && !Number.isNaN(toMs) && at > toMs) {
          return false;
        }
        return true;
      });
    }
  };
}
